import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter, ErrorComponent } from '@tanstack/react-router';
import { Provider, useSetAtom } from 'jotai';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import * as Sentry from '@sentry/react';

import { Toaster } from '@/components/ui/toaster';
import { testModeAtom } from '@/store';
import { useEffect } from 'react';
import { ApiError } from '@/lib/api-error';

// Import i18n initialization
import './i18n';

import { routeTree } from './routeTree.gen';
import './index.css';
import { UserProvider } from './contexts/user.context';

const env = import.meta.env;

const NODE_ENV = env.VITE_NODE_ENV;
// Initialize PostHog (production and staging only)
if (NODE_ENV === 'production' || NODE_ENV === 'staging') {
  posthog?.init(env.VITE_POSTHOG_API_KEY!, {
    api_host: env.VITE_POSTHOG_HOST!,
  });
}
if (NODE_ENV === 'production' || NODE_ENV === 'staging') {
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing — sample a fraction of sessions; 1.0 traced/profiled/replayed
    // every prod session (Sentry quota burn, and replays capture user input
    // on a billing product). Error replays stay at 100%.
    tracesSampleRate: 0.2,
    tracePropagationTargets: ['localhost', /^https:\/\/app\.glitchads\.ai/, /^https:\/\/staging\.glitchads\.ai/],
    profilesSampleRate: 0.2,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: NODE_ENV,
  });
}
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      retry: (failureCount, error) => {
        // Prefer the typed status when the endpoint throws an ApiError: only 5xx is
        // transient, so 4xx (auth, validation, not-found) must never be retried.
        if (error instanceof ApiError) {
          if (error.status < 500) {
            return false;
          }
          return failureCount < 3;
        }
        // Don't retry on auth/permission errors
        if (error instanceof Error && error.name === 'UnauthorizedError') {
          return false;
        }
        // Fallback for endpoints not yet migrated to assertOk: substring-match the
        // legacy hand-written messages (audit finding 180 — being phased out).
        if (error instanceof Error) {
          const msg = error.message.toLowerCase();
          if (msg.includes('unauthorized') || msg.includes('forbidden') || msg.includes('not permitted') || msg.includes('access denied')) {
            return false;
          }
        }
        return failureCount < 3;
      },
    },
  },
});
const GOOGLE_ADS_CLIENT_ID = env.VITE_GOOGLE_ADS_CLIENT_ID!;
const router = createRouter({
  routeTree,
  defaultErrorComponent: ({ error }) => <ErrorComponent error={error} />,
  context: {
    queryClient,
  },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface HistoryState {
    impersonate?: { action?: string };
  }
}


// Dev-only bridge for Playwright: exposes a window hook that flips the in-memory test-mode atom
// (#6257 — Meta UI is test-mode-gated until Meta App Review clears, and specs driving Meta
// surfaces need the gate open). Lives inside the Jotai <Provider> so it writes the same store the
// app reads; excluded from production bundles by the DEV guard.
function DevTestModeBridge() {
  const setTestMode = useSetAtom(testModeAtom);
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as unknown as { __glitchSetTestMode?: (v: boolean) => void }).__glitchSetTestMode = setTestMode;
    return () => {
      delete (window as unknown as { __glitchSetTestMode?: (v: boolean) => void }).__glitchSetTestMode;
    };
  }, [setTestMode]);
  return null;
}

const rootElement = document.getElementById('app')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <PostHogProvider client={posthog}>
        <QueryClientProvider client={queryClient}>
          <Provider>
            <GoogleOAuthProvider clientId={GOOGLE_ADS_CLIENT_ID}>
              <UserProvider>
                <DevTestModeBridge />
                <Toaster />
                <RouterProvider router={router} />
              </UserProvider>
            </GoogleOAuthProvider>
          </Provider>
        </QueryClientProvider>
      </PostHogProvider>
    </StrictMode>,
  );
}
